/**
 * useCanonical — keeps <link rel="canonical"> in sync with the current
 * clean, indexable URL on every SPA navigation.
 *
 * Why this exists
 * ---------------
 * The CRA shell `public/index.html` ships with a hard-coded canonical
 * pointing at the homepage. A postbuild prerender script rewrites that
 * canonical to the correct per-route URL for a whitelist of public
 * routes. For routes not in the whitelist, Nginx falls back to the SPA
 * shell and the stale homepage canonical would leak to crawlers. This
 * hook fixes that at runtime and also strips tracking/query params
 * (utm_*, ref, Stripe session_id, etc.) from the canonical.
 *
 * Rules enforced
 * - Absolute canonical using the non-www production domain.
 * - Pathname only — no query string, no fragment.
 * - Trailing slash preserved only for "/".
 * - Non-indexable routes (dashboard, auth, subscribe, 2FA, api, meta
 *   callbacks, etc.) are NOT given a self-referencing canonical; the
 *   tag is left pointing at the production homepage, matching robots.txt
 *   Disallow rules. This prevents a non-indexable surface from ever
 *   accidentally advertising itself as canonical.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export const CANONICAL_BASE = "https://zynthoro.ai";

// Routes we actively disallow in robots.txt or that should never be
// indexed as themselves. These keep the shell's homepage canonical.
const NON_INDEXABLE_PREFIXES = [
  "/dashboard",
  "/meetings",
  "/subscribe",
  "/login",
  "/signup",
  "/verify-email",
  "/forgot-password",
  "/reset-password",
  "/claim-account",
  "/accept-invite",
  "/2fa",
  "/api",
  "/onboarding",
];

const NON_INDEXABLE_EXACT = new Set([
  "/meta-callback",
]);

export function isIndexablePath(pathname) {
  if (!pathname || typeof pathname !== "string") return false;
  if (NON_INDEXABLE_EXACT.has(pathname)) return false;
  for (const prefix of NON_INDEXABLE_PREFIXES) {
    if (pathname === prefix || pathname.startsWith(prefix + "/")) return false;
  }
  return true;
}

export function computeCanonical(pathname) {
  // Normalise trailing slash: keep "/" exact, strip trailing slash for
  // every other path so "/pricing" and "/pricing/" resolve to the same
  // canonical.
  let p = pathname || "/";
  if (p !== "/" && p.endsWith("/")) p = p.replace(/\/+$/, "");
  return `${CANONICAL_BASE}${p}`;
}

function setCanonicalHref(href) {
  let tag = document.querySelector('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", "canonical");
    document.head.appendChild(tag);
  }
  if (tag.getAttribute("href") !== href) {
    tag.setAttribute("href", href);
  }
}

export default function useCanonical() {
  const { pathname } = useLocation();
  useEffect(() => {
    const href = isIndexablePath(pathname)
      ? computeCanonical(pathname)
      : `${CANONICAL_BASE}/`;
    setCanonicalHref(href);
  }, [pathname]);
}
